import compareVersions from 'compare-versions';
import lodash from 'lodash';
import APP from 'stores/APP';

export * from './currency';

// 脱敏
export const getMaskText = (fullText?: string) => {
    if (!fullText) {
        return '';
    }

    const stringTextLen = fullText.length;
    const last4Digits = fullText.slice(-4);

    // 最多只有四个****
    const maskLen = stringTextLen > 4 ? 8 : stringTextLen + 4;

    return last4Digits.padStart(maskLen, '*');
};

/**
 * 比较版本信息
 * @param minimumVersion 最低版本号要求, `${number}.${number}.${number}.${number}`
 * @param mockVersion 模拟版本号，用于测试
 * @returns Boolean, 当前版本低于 minimumVersion 时，返回 true
 */
export function lessVersion(minimumVersion: string, mockVersion?: string) {
    const version = mockVersion || APP.appVer;
    let isVersionLow = true;

    if (version) {
        // 比较 version 是否小于 versionKey
        isVersionLow = compareVersions.compare(version, minimumVersion, '<');
    }

    return isVersionLow;
}

/**
 * 对象扁平化
 * @param data 需要扁平化的数据
 * @param predicate 强制终止遍历函数，接收当前 item 的值，返回 true 时结束更深层遍历, 默认遍历到最深处
 * @param iteratee 迭代器，用于更改每次遍历的返回值，接收 5 个参数 childAcc：已经合并了的数据 | key：数据获取循环的 key | childKey：数据循环时遍历当前值的 key | separator：分隔符 | flattenedChild：原数据，用于最终赋值
 * @returns 返回对象的 key 为每层 key 值累加
 * @example { a: { f: 12 }, b: 2, c: { g: 23 }, d: [1,2,3] } => { 'a.f': 12, b: 2, 'c.g': 23, 'd.0': 1, 'd.1': 2, 'd.2': 3 }
 */
export function objectFlat<T = Record<string, any>>(
    data: T,
    predicate: (value: any) => boolean = () => false,
    iteratee?: (data: { childAcc: T; childKey: string; key: string; separator: string; flattenedChild: T }) => any,
    separator = '.'
): T {
    if (data) {
        const flat = (obj): T =>
            Object.keys(obj).reduce<T>((acc, key) => {
                // 判断是否为 object，普通类型直接返回
                if (typeof obj[key] !== 'object') {
                    return {
                        ...acc,
                        [key]: obj[key]
                    };
                }

                let flattenedChild;

                // 判断是否结束
                if (predicate(obj[key])) {
                    // 如果满足结束条件，复制当前值
                    flattenedChild = lodash.clone(obj[key]);
                } else {
                    // 否则继续遍历
                    flattenedChild = flat(obj[key]);
                }

                return {
                    ...acc,
                    ...Object.keys(flattenedChild).reduce((childAcc, childKey) => {
                        // 进行数据拼接
                        // 如果存在迭代器，将返回迭代器的返回值，否则走默认拼接方式
                        return iteratee
                            ? iteratee({ childAcc, childKey, key, separator, flattenedChild })
                            : { ...childAcc, [`${key}${separator}${childKey}`]: flattenedChild[childKey] };
                    }, {} as T)
                };
            }, {} as T);

        return flat(data);
    }

    return data;
}
